import type { RingState } from "../../shared/api-contract";
import type { RingSetDoc } from "./ringState";
import { apiGet, apiPost } from "../../features/shared/apiClient";

/**
 * Save/share/restore for Ring Builder designs — the engagement `designStore`
 * carried over to the merged v37 `RingState` (the viewer's
 * `serializeEngagementDesign` now round-trips the whole ring, band half
 * included).
 *
 * **Mode B ("local") is the shipping path.** The design JSON is
 * deflate-compressed and base64url-encoded straight into the URL as
 * `?design=z:<blob>` — no server, no auth, works for anonymous `/dev/ring`
 * sessions, and a small localStorage "recents" list backs it for the current
 * browser. Nothing has to exist server-side for save/share to work.
 *
 * Mode A ("vault") POSTs the design to `/ring/designs` for a short `?design=<id>`
 * link instead. That route is **not implemented in `server/`**, so it is off —
 * see `VAULT_DESIGNS` below. It is not a failure path we fall out of; it is a
 * feature that has not shipped.
 */

const RECENTS_KEY = "ring_design_recents";
const RECENTS_MAX = 10;
const LOCAL_PREFIX = "z:";

export type SaveMode = "vault" | "local";

export interface SaveResult {
  mode: SaveMode;
  /** What was left out of the token, in sentences for the user (empty/absent when nothing was). */
  left?: string[];
  /** Opaque token stored in the `?design=` query param. */
  token: string;
  /** Full shareable URL for the current page. */
  shareUrl: string;
}

export interface RecentDesign {
  token: string;
  mode: SaveMode;
  url: string;
  savedAt: number;
  /** Short label for the recents list — center stone shape + carat, or the band. */
  label: string;
}

/**
 * Is there a server-side designs collection? `server/` has no `/ring/designs`
 * route, so: no. Flip this to `true` the day it ships — the request code below
 * is written against the `vaultApi.ts` envelope and needs nothing else. Until
 * then Mode B is the only path, on purpose, rather than by way of a POST we
 * know will 404.
 *
 * Typed `boolean` (not the literal) so the guarded branch stays live code.
 */
const VAULT_DESIGNS: boolean = false;

const DESIGNS_PATH = "/ring/designs";

// ─── Mode B: URL compression ─────────────────────────────────────────────

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(s.length + ((4 - (s.length % 4)) % 4), "=");
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function compress(json: string): Promise<string> {
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream("deflate"));
  const buf = await new Response(stream).arrayBuffer();
  return bytesToBase64Url(new Uint8Array(buf));
}

async function decompress(blob: string): Promise<string> {
  const stream = new Blob([base64UrlToBytes(blob)]).stream().pipeThrough(new DecompressionStream("deflate"));
  const buf = await new Response(stream).arrayBuffer();
  return new TextDecoder().decode(buf);
}

// ─── Recents (localStorage) ───────────────────────────────────────────────

export function getRecentDesigns(): RecentDesign[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as RecentDesign[];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function pushRecent(entry: RecentDesign) {
  try {
    const next = [entry, ...getRecentDesigns().filter((r) => r.token !== entry.token)].slice(0, RECENTS_MAX);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* localStorage unavailable (private mode / quota) — recents are best-effort */
  }
}

function shareUrlFor(token: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set("design", token);
  return url.toString();
}

function labelFor(doc: RingSetDoc): string {
  const state: RingState | undefined = doc.rings[0];
  if (!state) return "ring design";
  const c = state.engagement?.enabled ? state.engagement.centerStone : null;
  const one = c ? `${c.shape} ${c.caratCt.toFixed(2)}ct` : `${state.widthMM}mm band · US ${state.ringSizeUS}`;
  return doc.rings.length > 1 ? `${one} + ${doc.rings.length - 1}` : one;
}

// ─── What never travels in a token ───────────────────────────────────────

/**
 * The set as it may go into a share token (v67, band designs W4).
 *
 * A RASTER logo uploaded while signed out lives in the state as a `data:` URL
 * — on this device only. It never goes into a token: it would make the link
 * hundreds of KB long, and it is the user's private artwork, not a design
 * parameter. The ring keeps it here; the shared copy drops it and says so.
 * An SVG logo (normalised path data, ≤ 16 KB) and a Vault asset URL do travel.
 * Pure — the input is never mutated.
 */
export function shareSafeDoc(doc: RingSetDoc): { doc: RingSetDoc; left: string[] } {
  const left: string[] = [];
  const rings = doc.rings.map((s, i) => {
    const logo = s.engraving?.logo;
    if (!logo || typeof logo.url !== "string" || !/^data:/i.test(logo.url)) return s;
    // A data: picture never travels — also when an svg rides beside it (the
    // viewer keeps both when both are valid, and svg wins on the ring; the
    // picture would still have gone into the token).
    if (logo.svg) {
      const { url: _url, ...kept } = logo;
      return { ...s, engraving: { ...s.engraving, logo: kept } };
    }
    left.push(`${doc.rings.length > 1 ? `Ring ${i + 1}: t` : "T"}he uploaded logo picture stays on this device; sign in to keep it in your Vault and share it.`);
    const { logo: _drop, ...engraving } = s.engraving;
    return { ...s, engraving: engraving as RingState["engraving"] };
  });
  const changed = rings.some((s, i) => s !== doc.rings[i]);
  return { doc: changed ? { ...doc, rings } : doc, left };
}

// ─── Public API ───────────────────────────────────────────────────────────

/** Save a design as a shareable token. Always succeeds: without the Vault
 * route the design travels inside the URL (Mode B), which needs no server.
 *
 * v43: the document is the whole ring SET (`RingSetDoc`), not the ring being
 * edited. `parseRingSetDoc` still reads the single-ring tokens already minted. */
export async function saveDesign(docIn: RingSetDoc): Promise<SaveResult> {
  const { doc, left } = shareSafeDoc(docIn);
  const label = labelFor(doc);

  if (VAULT_DESIGNS) {
    try {
      const { id } = await apiPost<{ id: string }>(DESIGNS_PATH, { design: doc });
      const result: SaveResult = { mode: "vault", token: id, shareUrl: shareUrlFor(id), left };
      pushRecent({ token: id, mode: "vault", url: result.shareUrl, savedAt: Date.now(), label });
      return result;
    } catch (err) {
      console.warn(`[ring] ${DESIGNS_PATH} save failed — sharing via the URL instead:`, err);
    }
  }

  const token = `${LOCAL_PREFIX}${await compress(JSON.stringify(doc))}`;
  const result: SaveResult = { mode: "local", token, shareUrl: shareUrlFor(token), left };
  pushRecent({ token, mode: "local", url: result.shareUrl, savedAt: Date.now(), label });
  return result;
}

/** Resolve a `?design=` token (from either mode) back into a design JSON
 * string, for `parseRingSetDoc` + `restoreRingSet`. Returns null if there's no
 * token, or it can't be resolved — a bare id with no Vault route behind it
 * included. */
export async function loadDesignByToken(token: string): Promise<string | null> {
  try {
    if (token.startsWith(LOCAL_PREFIX)) return await decompress(token.slice(LOCAL_PREFIX.length));
    const { design } = await apiGet<{ design: RingSetDoc | RingState }>(`${DESIGNS_PATH}/${token}`);
    return JSON.stringify(design);
  } catch {
    return null;
  }
}

/** Convenience: pulls `design` off a location.search-style query string. */
export async function loadDesignFromSearch(search: string): Promise<string | null> {
  const token = new URLSearchParams(search).get("design");
  if (!token) return null;
  return loadDesignByToken(token);
}
